import DropdownSelect from "#components/DropdownSelect/index.js";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "components/LoadingButton";
import Dialog from "components/NoScrollDialog";
import TranslatableTextField from "components/TranslatableTextField";
import { useLanguage } from "contexts/language.context";
import * as React from "react";
import accountsService from "services/accounts-service";
import competencyService from "services/competency-service";

function AccountSelectionGrid({ accountId, onChange, dropdownOptions }) {
  const { label } = useLanguage();
  const can = usePermissionMonitor();

  return (
    <>
      <Grid item xs={can("isAdmin") ? 6 : 12}>
        <DropdownSelect
          label={label("account-singular")}
          items={dropdownOptions.accounts}
          initialValue={accountId || ""}
          disabled={can("isAdmin") ? !accountId : false}
          required
          onChange={onChange}
        />
      </Grid>
      {can("isAdmin") ? (
        <Grid item xs={6}>
          <FormControlLabel
            label={label("Available for everyone")}
            control={
              <Checkbox
                checked={!accountId}
                onChange={(e) => {
                  if (!e.target.checked) {
                    onChange(dropdownOptions?.accounts?.[0]);
                  } else {
                    onChange(null);
                  }
                }}
              />
            }
          />
        </Grid>
      ) : null}
    </>
  );
}

export default function CompetenciesDialog({
  isOpen,
  onSubmit,
  data,
  mode,
  onClose,
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [competency, setCompetency] = React.useState({
    name: { en: "" },
    referenceCode: "",
  });
  const [options, setOptions] = React.useState({ accounts: [] });
  const { label } = useLanguage();
  const [error, setError] = React.useState(null);

  async function fetch() {
    let [ok, data] = await accountsService.getAccounts({ raw: true });
    if (ok) {
      setOptions({ ...options, accounts: data });
    }

    if (mode == "create") {
      [ok, data] = await competencyService.getNextCompetencyReferenceCode();
      if (ok) {
        setCompetency((competency) => ({
          ...competency,
          referenceCode: data.referenceCode,
        }));
      }
    }
  }

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setCompetency({
      name: { en: "" },
    });
  }, [isOpen, mode]);

  React.useEffect(() => {
    if (data) setCompetency({ ...data, name: data?.allLanguageName });
  }, [data, mode, isOpen]);

  React.useEffect(() => {
    setError(null);
    if (isOpen) fetch();
  }, [mode, isOpen]);

  console.log("Competency: ", competency);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const dropdownOptions = React.useMemo(() => {
    return {
      accounts:
        options.accounts?.map((v) => ({ id: v.id, label: v.name })) || [],
    };
  }, [options]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(competency);

          if (result.error) {
            setError(result.error);
          } else {
            setError(null);
            handleClose();
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {mode == "edit" ? label("edit-competency") : label("create-competency")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1} sx={{ pt: 1.5 }}>
          <Grid item xs={8}>
            <TranslatableTextField
              autoFocus
              required
              id="name"
              name="name"
              label={label("name-singular")}
              type="text"
              values={competency.name}
              onChange={(e) => {
                setCompetency({
                  ...competency,
                  name: e.target.value,
                });
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              required
              label={label("reference-code-singular")}
              value={competency?.referenceCode || ""}
              onChange={(e) => {
                setCompetency((competency) => {
                  return { ...competency, referenceCode: e.target.value };
                });
              }}
              error={error?.fields === "competency_referenceCode_key"}
              helperText={
                error?.fields === "competency_referenceCode_key" &&
                error?.message
              }
            />
          </Grid>

          <AccountSelectionGrid
            accountId={competency.accountId}
            onChange={(newValue) => {
              setCompetency({ ...competency, accountId: newValue?.id });
            }}
            dropdownOptions={dropdownOptions}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {mode == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
